import { useEffect, useRef, useState } from 'react'
import { AlertCircle, ArrowRight, Check, Compass, Copy } from '@/ui/icons'
import { Button } from '@/ui/components'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'
import { UpgradePrompt } from '@/modules/subscriptions/components/UpgradePrompt'
import { RangerMark } from './RangerMark'
import { RangerMarkdownContent } from './RangerMarkdownContent'
import { RangerFunctionResultCard } from './RangerFunctionResultCard'
import { RangerSuggestions } from './RangerSuggestions'
import { RangerComposer, type RangerToolControls } from './RangerComposer'
import type { RangerChatMessage } from '../hooks/useRanger'
import type { RangerFunctionCallResult } from '@/shared/api/ranger'

interface RangerChatProps {
  tripId?: string
  /** Shown under the composer so it's clear which trip Ranger is looking at. */
  tripName?: string
  messages: RangerChatMessage[]
  sendMessage: (message: string) => Promise<void>
  loading: boolean
  error: string | null
  clearError: () => void
  /** Enables the Tools menu. Left out where a tool's flow doesn't fit, like the mobile modal. */
  tools?: RangerToolControls
  /** Focus the composer on mount. Off where the chat opens without the user asking. */
  autoFocus?: boolean
  /** Increment to move focus to the composer. */
  focusRequest?: number
}

/** The same failure from several tool calls only needs saying once. */
function withoutRepeatedFailures(calls: RangerFunctionCallResult[]): RangerFunctionCallResult[] {
  const seenErrors = new Set<string | undefined>()
  return calls.filter((call) => {
    if (call.success) return true
    // A skipped invite is a note to the model, not something the user needs to see.
    if (call.name === 'invite_friends' && call.error?.startsWith('Not sent.')) return false
    if (seenErrors.has(call.error)) return false
    seenErrors.add(call.error)
    return true
  })
}

/** Stay pinned to the newest message unless the user has scrolled up further than this. */
const STICK_TO_BOTTOM_THRESHOLD_PX = 120

function composerPlaceholder(isBuildingTrip: boolean, tripBuilt: boolean, tripId?: string): string {
  if (tripBuilt) return 'Tell Ranger who else is coming...'
  if (isBuildingTrip) return 'Moab, March 5 to 7, with Jake and Sam'
  return tripId ? 'Ask about this trip...' : 'Ask Ranger anything...'
}

function composerNote(isBuildingTrip: boolean, tripBuilt: boolean, tripId?: string, tripName?: string): string {
  if (tripBuilt) return 'Invites only go out when you ask'
  if (isBuildingTrip) return 'Ranger sets up the trip, then invites your crew'
  if (tripId) return tripName ? `Ranger can see ${tripName}` : 'Ranger can see this trip'
  return 'Routes, camps, gear, weather and more'
}

export function RangerChat({
  tripId,
  tripName,
  messages,
  sendMessage,
  loading,
  error,
  clearError,
  tools,
  autoFocus = true,
  focusRequest = 0,
}: RangerChatProps) {
  const { status } = useSubscription()
  const [input, setInput] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const canUseRanger = status?.features.canUseRanger ?? false
  const isBuildingTrip = tools?.activeTool === 'build_trip'
  const tripBuilt = isBuildingTrip && Boolean(tools?.tripBuilt)

  // A reply glides into view. A different chat just appears at its latest message,
  // rather than scrolling through its whole length.
  const firstMessageIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const el = scrollRef.current
    const firstMessageId = messages[0]?.id
    const isSameChat = firstMessageIdRef.current === firstMessageId
    firstMessageIdRef.current = firstMessageId
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: isSameChat ? 'smooth' : 'auto' })
  }, [messages, loading])

  // Cards can grow after they render (the invite card loads its friends), so a scroll on
  // new messages alone would leave their actions below the fold.
  const threadRef = useRef<HTMLDivElement>(null)
  const hasMessages = messages.length > 0
  useEffect(() => {
    const thread = threadRef.current
    const el = scrollRef.current
    if (!thread || !el) return

    let lastHeight = thread.offsetHeight
    const observer = new ResizeObserver(() => {
      const grewBy = thread.offsetHeight - lastHeight
      lastHeight = thread.offsetHeight
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      if (grewBy > 0 && distanceFromBottom - grewBy < STICK_TO_BOTTOM_THRESHOLD_PX) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
      }
    })
    observer.observe(thread)
    return () => observer.disconnect()
  }, [hasMessages])

  useEffect(() => {
    if (autoFocus || focusRequest > 0) textareaRef.current?.focus()
  }, [autoFocus, focusRequest])

  // Starting a tool means the next thing the user does is answer Ranger.
  useEffect(() => {
    if (isBuildingTrip) textareaRef.current?.focus()
  }, [isBuildingTrip])

  const submitMessage = async (text: string) => {
    if (!text.trim() || loading || !canUseRanger) return
    setInput('')
    try {
      await sendMessage(text.trim())
    } catch {
      // The hook surfaces the error. Hand the text back so nothing has to be retyped.
      setInput(text)
    } finally {
      textareaRef.current?.focus()
    }
  }

  const handleCopy = async (message: RangerChatMessage) => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopiedId(message.id)
      setTimeout(() => setCopiedId((current) => (current === message.id ? null : current)), 1500)
    } catch {
      // Clipboard permission denied — no confirmation to show, just skip it.
    }
  }

  if (!canUseRanger) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto p-4">
          <UpgradePrompt
            title="Ranger AI Assistant"
            message="Ranger AI Assistant provides expert trip planning advice for overlanding and camping adventures. Upgrade to Pro to access this feature."
            feature="ranger"
            currentTier={status?.tier ?? 'free'}
            variant="default"
            className="mt-4"
          />
        </div>
      </div>
    )
  }

  const composer = (
    <RangerComposer
      ref={textareaRef}
      value={input}
      onChange={setInput}
      onSubmit={() => submitMessage(input)}
      loading={loading}
      placeholder={composerPlaceholder(isBuildingTrip, tripBuilt, tripId)}
      tools={tools}
    />
  )
  const note = (
    <p className="truncate px-1 text-center text-xs text-earth-600">
      {composerNote(isBuildingTrip, tripBuilt, tripId, tripName)}
    </p>
  )

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col px-4 pb-4">
        <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto">
          <div className="my-auto flex w-full flex-col items-center gap-4 py-4">
            <RangerMark entrance className="h-10 w-10 text-adventure-600" />
            <h3 className="text-xl font-semibold text-foreground">How can I help?</h3>
            <div className="flex w-full flex-col gap-2">
              {composer}
              {note}
            </div>
            <RangerSuggestions tripId={tripId} onSelect={submitMessage} disabled={loading} />
            {error && <ErrorNotice message={error} onDismiss={clearError} />}
          </div>
        </div>
        {tools && (
          <button
            type="button"
            onClick={() => tools.onStart('build_trip')}
            className="mt-3 flex w-full flex-shrink-0 items-center gap-3 rounded-xl border border-earth-200 p-3 text-left transition-colors hover:bg-earth-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-adventure-100">
              <Compass className="h-5 w-5 text-adventure-700" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-foreground">Build a trip</span>
              <span className="block truncate text-xs text-earth-600">
                Set it up and invite your crew
              </span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-earth-600" aria-hidden />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollRef} role="log" aria-live="polite" className="flex-1 overflow-y-auto px-4 py-2">
          <div ref={threadRef} className="flex flex-col gap-5">
            {messages.map((message) => (
              <div key={message.id} className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                {message.role === 'user' ? (
                  <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-adventure-600 px-4 py-2.5 text-sm text-white">
                    <p className="whitespace-pre-wrap break-words">{message.content}</p>
                  </div>
                ) : (
                  <div className="group w-full max-w-[92%]">
                    <div className="mb-1 flex items-center gap-1.5">
                      <RangerMark className="h-4 w-4 text-adventure-600" />
                      <span className="text-xs font-medium text-gray-500">Ranger</span>
                      <button
                        type="button"
                        onClick={() => handleCopy(message)}
                        aria-label="Copy response"
                        className="ml-auto text-gray-400 opacity-0 transition-opacity hover:text-gray-700 group-hover:opacity-100 dark:hover:text-foreground"
                      >
                        {copiedId === message.id ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    <RangerMarkdownContent content={message.content} className="text-sm text-foreground" />
                    {message.functionCalls && message.functionCalls.length > 0 && (
                      <div className="mt-2.5 flex flex-col items-start gap-1.5">
                        {withoutRepeatedFailures(message.functionCalls).map((call, i) => (
                          <RangerFunctionResultCard key={i} call={call} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="py-1">
                <RangerMark thinking className="h-6 w-6 text-adventure-600" />
                <span className="sr-only">Ranger is thinking</span>
              </div>
            )}
          </div>

        {error && <ErrorNotice message={error} onDismiss={clearError} />}
      </div>

      <div className="flex flex-shrink-0 flex-col gap-2 p-3">
        {composer}
        {note}
      </div>
    </div>
  )
}

function ErrorNotice({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="mt-4 flex w-full items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/40 dark:bg-red-950/20">
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
      <div className="flex-1">
        <p role="alert" className="text-sm text-red-800 dark:text-red-300">{message}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="mt-2 h-auto p-0 text-xs text-red-600 hover:text-red-700"
        >
          Dismiss
        </Button>
      </div>
    </div>
  )
}
